import { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import axios from "axios";
import { Briefcase, GraduationCap, Wrench, MapPin, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import ApplicationForm from "@/components/ApplicationForm";
import RecruitTestimonials from "@/components/RecruitTestimonials";
import RecruitGallery from "@/components/RecruitGallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { job: Briefcase, internship: Wrench, training: GraduationCap };

const Recruit = () => {
  const { t } = useLanguage();
  const r = t.recruit;
  const [jobs, setJobs] = useState([]);
  const [preset, setPreset] = useState(null);
  useEffect(() => { axios.get(`${API}/jobs`).then((res) => setJobs(res.data)).catch(() => {}); }, []);

  const choose = (job) => {
    setPreset(job);
    document.getElementById("candidature")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-h-screen" data-testid="recruit-page">
      <Seo page="recruit" />
      <section className="relative overflow-hidden bg-mint/60 pb-20 pt-36">
        <div className="pointer-events-none absolute -left-24 top-10 h-[24rem] w-[24rem] rounded-full bg-cream blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead as="h1" overline={r.overline} title={r.title} accent={r.accent} sub={r.sub} />
          <div className="mt-14 grid gap-5 md:grid-cols-3" data-testid="recruit-pillars">
            {Object.entries(r.pillars).map(([k, v], i) => {
              const Icon = ICONS[k];
              return (
                <Reveal key={k} delay={0.1 * i}>
                  <button type="button" onClick={() => choose({ id: "", type: k })} data-testid={`recruit-pillar-${k}`} className="group flex h-full w-full flex-col items-start gap-4 rounded-3xl border border-brand/10 bg-white p-7 text-left shadow-xl shadow-brand/5 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40">
                    <span className={`flex h-12 w-12 items-center justify-center rounded-2xl text-white ${i === 1 ? "bg-brand" : "bg-gold"}`}><Icon className="h-6 w-6" /></span>
                    <h2 className="font-display text-xl font-bold text-forest">{v.title}</h2>
                    <p className="text-sm leading-relaxed text-brand-hover">{v.desc}</p>
                    <span className="mt-auto inline-flex items-center gap-2 pt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{r.apply}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="recruit-offers">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><h2 className="font-display text-2xl font-bold text-forest sm:text-3xl">{r.offersTitle} <span className="text-gold">({jobs.length})</span></h2></Reveal>
          {jobs.length === 0 ? (
            <Reveal delay={0.1}><p className="mt-6 rounded-3xl bg-mint p-8 text-base text-brand-hover" data-testid="recruit-no-offers">{r.noOffers}</p></Reveal>
          ) : (
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {jobs.map((j, i) => (
                <Reveal key={j.id} delay={0.08 * i}>
                  <article className="flex h-full flex-col rounded-3xl border border-brand/10 bg-white p-7 shadow-xl shadow-brand/5" data-testid={`recruit-offer-${i}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">{r.pillars[j.type]?.title || j.type}</span>
                      {j.domain && <span className="rounded-full bg-mint px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{j.domain}</span>}
                      {j.location && <span className="inline-flex items-center gap-1 text-xs text-brand-hover"><MapPin className="h-3.5 w-3.5" />{j.location}</span>}
                    </div>
                    <h3 className="font-display mt-4 text-xl font-bold text-forest">{j.title}</h3>
                    {j.description && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-brand-hover">{j.description}</p>}
                    <button type="button" onClick={() => choose(j)} data-testid={`recruit-offer-${i}-apply`} className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-forest px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-all hover:-translate-y-0.5 hover:bg-brand">{r.apply}<ArrowRight className="h-3.5 w-3.5" /></button>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <RecruitGallery />
      <RecruitTestimonials />

      <section id="candidature" className="bg-cream/60 py-20 scroll-mt-28" data-testid="recruit-form-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead overline={r.overline} title={r.formTitle} accent="" sub={r.formSub} />
          </div>
          <div className="lg:col-span-3"><ApplicationForm jobs={jobs} preset={preset} /></div>
        </div>
      </section>
    </main>
  );
};

export default Recruit;
